/**
 * Store Mapper Application
 * Handles store search functionality with map integration
 */

class StoreMapper {
  constructor() {
    this.storemapperDiv = document.getElementById('storemapper');
    this.pageType = window.storemapperPageType || '';
    this.observers = new Map();
    this.processedElements = new WeakSet();
    this.initialized = false;
    
    this.init();
  }

  /**
   * Get URL parameter by name
   * @param {string} name - Parameter name
   * @returns {string|null} Parameter value
   */
  getUrlParam(name) {
    const url = new URL(window.location.href);
    return url.searchParams.get(name);
  }

  /**
   * Initialize the application
   */
  init() {
    if (!this.storemapperDiv) return;

    // First check if storemapper is already loaded
    if (this.isStoremapperReady()) {
      this.setupApplication();
      return;
    }

    // Set up observer for dynamic loading
    const observer = new MutationObserver(() => {
      if (this.isStoremapperReady()) {
        this.setupApplication();
        observer.disconnect();
      }
    });

    observer.observe(this.storemapperDiv, { 
      childList: true, 
      subtree: true 
    });

    // Fallback: Also try to initialize after a delay
    setTimeout(() => {
      if (this.isStoremapperReady() && !this.initialized) {
        this.setupApplication();
        observer.disconnect();
      }
    }, 1000);
  }

  /**
   * Check if storemapper is ready with actual content
   */
  isStoremapperReady() {
    if (!this.storemapperDiv || this.storemapperDiv.innerHTML.trim() === '') {
      return false;
    }

    // Check for key storemapper elements that indicate it's fully loaded
    const searchButton = document.getElementById('storemapper-go');
    const searchBox = document.getElementById('storemapper-zip');
    const mapContainer = document.querySelector('.strmpr-layout-horizontal .strmpr-map');
    
    // Storemapper is ready if core elements exist
    return !!(searchButton && searchBox && mapContainer);
  }

  /**
   * Setup the complete application
   */
  setupApplication() {
    if (this.initialized) return;
    
    this.addPageTypeClasses();
    this.setupElements();
    this.setupEventListeners();
    this.setupFilters();
    this.filterDropdownOptions();
    this.setupInitialState();
    this.processUrlParameters();
    this.arrangeDivs();
    this.addInputLabels();
    
    // Set initial map design
    this.handleMapDesign();
    
    if (this.pageType === 'designer-event') {
      this.handleCustomFunctionalities();
    }

    this.initialized = true;
  }

  /**
   * Add page type specific classes
   */
  addPageTypeClasses() {
    if (this.pageType === 'designer-event') {
      this.storemapperDiv.classList.add('strmpr-designer-event');
    } else if (this.pageType === 'find-retailer') {
      this.storemapperDiv.classList.add('strmpr-find-retailer');
    }
  }

  /**
   * Setup DOM elements references
   */
  setupElements() {
    this.elements = {
      searchButton: document.getElementById('storemapper-go'),
      searchZipButton: document.getElementById('storemapper-zip'),
      viewMoreButton: document.querySelector('.strmpr-view-more-stores-button'),
      resetButton: document.querySelector('.strmpr-search .strmpr-search-box-wrapper .strmpr-search-reset'),
      storemapperResults: document.querySelector('.strmpr-layout-horizontal .strmpr-search-results'),
      mapContainer: document.querySelector('.strmpr-layout-horizontal .strmpr-map'),
      pageHeader: document.querySelector('.main-page-title'),
      filterBucket: document.querySelector('.strmpr-filter-bucket')
    };
  }

  /**
   * Setup event listeners
   */
  setupEventListeners() {
    const { searchButton, searchZipButton, viewMoreButton, resetButton } = this.elements;

    // Checkbox change events
    document.addEventListener('change', (e) => {
      if (e.target.classList.contains('storemapper-filter-checkbox')) {
        this.handleCustomFunctionalities();
      }
    });

    // Search related events
    if (searchZipButton) {
      if (this.pageType === 'find-retailer') {
        this.toggleSearchButton();
        searchZipButton.addEventListener('input', () => this.toggleSearchButton());
      }
    }

    if (searchButton) {
    searchButton.addEventListener('click', () => {
      this.handleMapDesignOnSearch();
      
      setTimeout(() => {
        this.handleCustomFunctionalities();
      }, 300);
      
      this.setupSearchResultsObserver();
    });
  }

    if (viewMoreButton) {
      viewMoreButton.addEventListener('click', () => {
        setTimeout(() => {
          this.handleCustomFunctionalities();
        }, 200);
      });
    }

    if (resetButton) {
      resetButton.addEventListener('click', () => {
        this.handleMapDesignOnReset();
        
        setTimeout(() => {
          this.handleCustomFunctionalities();
        }, 100);
      });
    }
  }

  /**
   * Toggle search button state based on zip input
   */
  toggleSearchButton() {
    const { searchButton, searchZipButton } = this.elements;
    const hasValue = searchZipButton?.value?.trim();
    
    if (hasValue) {
      searchButton?.removeAttribute('disabled');
    } else {
      searchButton?.setAttribute('disabled', '');
    }
  }

  /**
   * Setup dropdown filters with classes
   */
  setupFilters() {
    const dropdownButtons = document.querySelectorAll('.strmpr-dropdown-btn');
    
    dropdownButtons.forEach(button => {
      const span = button.querySelector('span');
      if (!span?.textContent) return;

      const text = span.textContent.trim()
        .toLowerCase()
        .replace(/\s+/g, '-')
        .replace(/[^a-z0-9-]/g, '-');

      const dropdownParent = button.closest('.strmpr-dropdown');
      dropdownParent?.classList.add(`strmpr-dropdown-${text}`);
    });

    this.setupTypeFilter();
  }

  /**
   * Filter dropdown options based on page type and storemapperCategories
   */
  filterDropdownOptions() {
    const dropdown = document.getElementById('storemapper-filter-drop-down-1');
    if (!dropdown) return;

    const options = dropdown.querySelectorAll('.strmpr-dropdown-option');
    if (!options.length) return;
    
    // Get categories from window global if available
    const categories = window.storemapperCategories || [];

    options.forEach(option => {
      const label = option.querySelector('label');
      if (!label) return;
      
      const labelText = label.textContent.trim();
      const labelTextLower = labelText.toLowerCase();
      
      // Skip filtering for "Select All" option
      if (labelTextLower === 'select all' || labelTextLower === 'all') {
        return;
      }
      
      // Hide by default, show only if matches a category
      if (categories.length > 0) {
        const matchesCategory = categories.some(category => 
          category.toLowerCase() === labelTextLower || 
          labelTextLower.includes(category.toLowerCase())
        );
        
        if (!matchesCategory) {
          option.style.display = 'none';
        } else {
          option.style.display = '';
        }
      }
    });
  }

  /**
   * Setup type filter text based on page type
   */
  setupTypeFilter() {
    const typeFilter = document.querySelector(
      '.strmpr-search-result-filters .strmpr-dropdown.strmpr-dropdown-product-type-event button span'
    );
    
    if (!typeFilter) return;

    const originalText = typeFilter.textContent.trim();
    const splitTexts = originalText.split('/');
    
    if (splitTexts.length < 2) return;

    const productTypeText = splitTexts[0].trim();
    const eventText = splitTexts[1].trim();

    if (this.pageType === 'designer-event') {
      typeFilter.textContent = eventText;
    } else if (this.pageType === 'find-retailer') {
      typeFilter.textContent = productTypeText;
    }
  }

  /**
   * Setup initial state for find-retailer page
   */
  setupInitialState() {
    if (this.pageType !== 'find-retailer') return;

    const { storemapperResults, mapContainer } = this.elements;

    // Hide results initially
    storemapperResults?.classList.add('hidden');

    // Hide existing map pins
    this.hideMapPins();

    // Setup observer for dynamically added pins
    if (mapContainer) {
      this.setupMapPinObserver(mapContainer);
    }
  }

  /**
   * Hide map pins
   */
  hideMapPins() {
    const mapPins = document.querySelectorAll('gmp-advanced-marker');
    mapPins.forEach(pin => pin.classList.add('hidden'));
  }

  /**
   * Setup observer for map pins
   */
  setupMapPinObserver(mapContainer) {
    const observer = new MutationObserver(() => {
      const newPins = document.querySelectorAll('gmp-advanced-marker:not(.observed)');
      newPins.forEach(pin => {
        pin.classList.add('hidden', 'observed');
      });
    });

    observer.observe(mapContainer, {
      childList: true,
      subtree: true
    });

    this.observers.set('mapPins', observer);
  }

  /**
   * Process URL parameters
   */
  processUrlParameters() {
    const collectionParam = this.getUrlParam('collection');
    if (!collectionParam) return;

    const options = document.querySelectorAll(
      '.strmpr-search-result-filters .strmpr-dropdown .strmpr-dropdown-option'
    );

    options.forEach(option => {
      const label = option.querySelector('label');
      const checkbox = option.querySelector('input[type="checkbox"]');

      if (label?.textContent.trim().toLowerCase() === collectionParam.trim().toLowerCase()) {
        if (!checkbox.checked) {
          checkbox.checked = true;
          checkbox.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
    });
  }

  /**
   * Arrange divs in proper structure
   */
  arrangeDivs() {
    this.movePageHeader();
    this.setupSearchButton();
  }

  /**
   * Move page header to search div
   */
  movePageHeader() {
    const { pageHeader } = this.elements;
    const searchDiv = this.storemapperDiv.querySelector('.strmpr-search');

    if (pageHeader && searchDiv) {
      pageHeader.classList.add('strmpr-header');
      searchDiv.insertBefore(pageHeader, searchDiv.firstChild);
    }
  }

  /**
   * Setup search button
   */
  setupSearchButton() {
    const { searchButton, filterBucket } = this.elements;

    if (searchButton && filterBucket) {
      searchButton.textContent = storemapperStrings.searchButton;
      filterBucket.appendChild(searchButton);
    }
  }

  /**
   * Add input labels
   */
  addInputLabels() {
    this.addShowMapToggle();
    this.addDistanceLabel();
    this.addSearchBoxLabel();
    this.addFiltersLabel();
  }

  /**
   * Add show map toggle
   */
  addShowMapToggle() {
    const { pageHeader } = this.elements;
    const searchDiv = this.storemapperDiv.querySelector('.strmpr-search');

    if (!searchDiv || !pageHeader) return;

    const showMapWrapper = this.createElement('div', 'show-map-wrapper', [
      this.createElement('span', 'show-map-label', [], 'Show map'),
      this.createElement('button', 'show-map-toggle', [
        this.createElement('span', 'show-map-toggle-circle')
      ])
    ]);

    const toggleButton = showMapWrapper.querySelector('.show-map-toggle');
    toggleButton.setAttribute('aria-pressed', 'true');
    
    toggleButton.addEventListener('click', () => this.toggleMap(toggleButton));

    pageHeader.insertAdjacentElement('afterend', showMapWrapper);
  }

  /**
   * Toggle map visibility
   */
  toggleMap(toggleButton) {
    const { mapContainer } = this.elements;
    if (!mapContainer) return;

    const isHidden = mapContainer.style.display === 'none';
    mapContainer.style.display = isHidden ? '' : 'none';
    toggleButton.setAttribute('aria-pressed', isHidden.toString());
  }

  /**
   * Add distance label
   */
  addDistanceLabel() {
    const distanceDiv = this.storemapperDiv.querySelector('.storemapper-distance .strmpr-distance-btn');
    if (distanceDiv) {
      const label = this.createElement('p', ['label-distance', 'storemapper-label'], [], storemapperStrings.within);
      distanceDiv.parentNode.insertBefore(label, distanceDiv);
    }
  }

  /**
   * Add search box label
   */
  addSearchBoxLabel() {
    const searchBoxDiv = this.storemapperDiv.querySelector('.strmpr-search-box-wrapper .strmpr-search-box');
    if (searchBoxDiv) {
      const label = this.createElement('p', ['label-search-box', 'storemapper-label'], [], storemapperStrings.zipCode);
      searchBoxDiv.parentNode.insertBefore(label, searchBoxDiv);
    }
  }

  /**
   * Add filters label
   */
  addFiltersLabel() {
    const { filterBucket } = this.elements;
    if (!filterBucket) return;

    const labelText = this.pageType === 'designer-event' ? storemapperStrings.searchDesign : storemapperStrings.searchRetailer;
    const label = this.createElement('p', ['label-search-result-filters', 'storemapper-label'], [], labelText);
    filterBucket.parentNode.insertBefore(label, filterBucket);
  }

  /**
   * Setup Search Results Observer
   */
  setupSearchResultsObserver() {
  const storemapperList = document.getElementById('storemapper-list');
  if (!storemapperList) return;

  if (this.observers.has('searchResults')) {
    this.observers.get('searchResults').disconnect();
  }

  const observer = new MutationObserver((mutations) => {
    let hasNewResults = false;
    
    mutations.forEach(mutation => {
      if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
        const newResults = Array.from(mutation.addedNodes).some(node => 
          node.nodeType === 1 && 
          (node.classList?.contains('strmpr-search-result') || 
           node.querySelector?.('.strmpr-search-result'))
        );
        if (newResults) {
          hasNewResults = true;
        }
      }
    });

    if (hasNewResults) {
      setTimeout(() => {
        this.handleCustomFunctionalities();
      }, 100);
    }
  });

  observer.observe(storemapperList, {
    childList: true,
    subtree: true
  });

  this.observers.set('searchResults', observer);
}

  /**
   * Handle custom functionalities
   */
  handleCustomFunctionalities() {
    clearTimeout(this.functionalitiesTimeout);
    this.functionalitiesTimeout = setTimeout(() => {
      this.customFunctionalities();
    }, 50);
  }

  /**
   * Custom functionalities after search
   */
  customFunctionalities() {
    const searchResults = document.querySelectorAll('.strmpr-search-result[data-idx]');
    if (searchResults.length === 0) {
      setTimeout(() => {
        const retryResults = document.querySelectorAll('.strmpr-search-result[data-idx]');
        if (retryResults.length > 0) {
          this.performCustomFunctionalities();
        }
      }, 100);
      return;
    }

    this.performCustomFunctionalities();
  }

  /**
   * Perfrom Custom functionalities after search
   */
  performCustomFunctionalities() {
    this.addFilterResultsHeader();
    this.handleResultsVisibility();
    this.processSearchResults();
    this.setupImageModal();
    this.handleMapDesign();
  }

  /**
   * Add filter results header
   */
  addFilterResultsHeader() {
    const { filterBucket } = this.elements;
    if (!filterBucket || filterBucket.querySelector('.strmpr-header-result')) return;

    const headerText = this.pageType === 'find-retailer' ? storemapperStrings.retailerResult : storemapperStrings.eventResult;
    const header = this.createElement('h4', ['strmpr-header', 'strmpr-header-result'], [], headerText);
    filterBucket.appendChild(header);
  }

  /**
   * Handle results visibility for find-retailer page
   */
  handleResultsVisibility() {
    if (this.pageType !== 'find-retailer') return;

    const { storemapperResults, resetButton, searchZipButton } = this.elements;
    const mapPins = document.querySelectorAll('gmp-advanced-marker');

    if (resetButton) {
      resetButton.addEventListener('click', () => {
        storemapperResults?.classList.add('hidden');
        mapPins.forEach(pin => pin.classList.add('hidden'));
      });
    }

    // Show results if search is active
    if (searchZipButton?.value?.trim()) {
      storemapperResults?.classList.remove('hidden');
      mapPins.forEach(pin => pin.classList.remove('hidden'));
    }
  }

  /**
   * Process all search results efficiently in a single loop
   */
  processSearchResults() {
    const searchResults = document.querySelectorAll('.strmpr-search-result[data-idx]');
    if (searchResults.length === 0) return;

    // Process all search result related functionality in one loop
    searchResults.forEach((resultDiv, index) => {
      this.processIndividualResult(resultDiv, index);
    });

    // Handle elements that need to be processed separately
    this.processViewMoreButton();
    this.addResultDividers();
    this.processEventDates();
    this.processEventTypes();
  }

  /**
   * Process individual search result
   */
  processIndividualResult(resultDiv, index) {
    const idx = resultDiv.getAttribute('data-idx');

    // Setup map interaction
    this.setupMapInteraction(resultDiv, idx);

    // Add store number
    this.addStoreNumber(resultDiv, index);

    // Wrap store info elements
    this.wrapStoreInfo(resultDiv);

    // Handle featured retailers
    this.handleFeaturedRetailer(resultDiv);

    // Process booking buttons
    this.processBookingButtons(resultDiv);

    // Setup image modal for logo
    this.setupLogoImageModal(resultDiv);
    
    // Add custom div below request booking button
    this.addDivBelowRequestBooking(resultDiv);
    
    // Add custom div below request booking button
    this.addDivBelowRequestBooking(resultDiv);
  }

  /**
   * Setup map interaction for result
   */
  setupMapInteraction(resultDiv, idx) {
    const mapDiv = document.querySelector(`.storemapper_view[data-idx="${idx}"]`);
    if (!mapDiv) return;

    // Remove any existing event listeners
    const existingHandler = resultDiv._mapInteractionHandler;
    if (existingHandler) {
      resultDiv.removeEventListener('mouseenter', existingHandler);
      resultDiv.removeEventListener('click', existingHandler);
    }

    // Create a new handler
    const mapInteractionHandler = (event) => {
      if (event.target.tagName === 'BUTTON' || 
          event.target.tagName === 'A' || 
          event.target.closest('button') || 
          event.target.closest('a') ||
          event.target.hasAttribute('onclick')) {
        return;
      }

      if (event.type === 'mouseenter' && 'ontouchstart' in window) {
        return;
      }

      mapDiv.click();
    };

    // Store the handler reference for cleanup
    resultDiv._mapInteractionHandler = mapInteractionHandler;

    // Add event listeners
    resultDiv.addEventListener('mouseenter', mapInteractionHandler);
    resultDiv.addEventListener('click', mapInteractionHandler);
  }

  /**
   * Add store number to result
   */
  addStoreNumber(resultDiv, index) {
    const nameDiv = resultDiv.querySelector('.strmpr-field-name');
    if (!nameDiv || nameDiv.querySelector('.store-number')) return;

    const numberSpan = this.createElement('span', 'store-number', [], `${index + 1} `);
    nameDiv.prepend(numberSpan);
  }

  /**
   * Wrap store info elements
   */
  wrapStoreInfo(resultDiv) {
    const bucket = resultDiv.querySelector('.strmpr-field-bucket-general');
    if (!bucket) return;

    const directions = bucket.querySelector('.strmpr-field-directions.storemapper_directions');
    const url = bucket.querySelector('.strmpr-field-url');

    if (directions && url && !directions.parentElement.classList.contains('strmpr-store__info')) {
      const wrapper = this.createElement('div', 'strmpr-store__info');
      bucket.insertBefore(wrapper, directions);
      wrapper.appendChild(directions);
      wrapper.appendChild(url);
    }
  }

  /**
   * Handle featured retailer styling
   */
  handleFeaturedRetailer(resultDiv) {
    const featured = resultDiv.querySelector('.strmpr-field-custom.featured_retailer');
    if (!featured) return;

    featured.classList.remove('hidden');

    const featuredText = featured.textContent.trim();
    if (featuredText.match(/Featured:\s*Y/i) || featuredText === storemapperStrings.featured) {
      featured.textContent = storemapperStrings.featured;
    } else {
      featured.classList.add('hidden');
    }
  }

  /**
   * Process booking buttons
   */
  processBookingButtons(resultDiv) {
    this.processBookAppointmentButtons(resultDiv);
    this.processRequestAppointmentButtons(resultDiv);
  }

  /**
   * Process book appointment buttons
   */
  processBookAppointmentButtons(resultDiv) {
    const bookButtons = resultDiv.querySelectorAll('.strmpr-field-custom.direct_booking button');
    
    bookButtons.forEach(button => {
      if (button.dataset.parameterized) return;

      const retailerIdField = resultDiv.querySelector('.strmpr-field-custom.retailer_id');
      const bridalRetailIdField = resultDiv.querySelector('.strmpr-field-custom.bridal_retailid');
      const retailerNameField = resultDiv.querySelector('.strmpr-field-bucket-general .strmpr-field-name');

      const retailerId = this.extractFieldValue(retailerIdField);
      const bridalRetailId = this.extractFieldValue(bridalRetailIdField);
      const retailerName = retailerNameField ? this.extractRetailerName(retailerNameField) : '';

      if (retailerId || bridalRetailId) {
        this.updateButtonOnclick(button, { 
          retailerId, 
          bridalretailid: bridalRetailId ,
          retailerName: retailerName
        });
        button.dataset.parameterized = 'true';
      }
    });
  }

  /**
   * Process request appointment buttons
   */
  processRequestAppointmentButtons(resultDiv) {
    const requestButtons = resultDiv.querySelectorAll('.strmpr-field-custom.req_booking button');
    
    requestButtons.forEach(button => {
      if (button.dataset.parameterized) return;

      const retailerIdField = resultDiv.querySelector('.strmpr-field-custom.retailer_id');
      const emailField = resultDiv.querySelector('.strmpr-field-bucket-general .strmpr-field-email a');
      const retailerNameField = resultDiv.querySelector('.strmpr-field-bucket-general .strmpr-field-name');
      
      const retailerId = this.extractFieldValue(retailerIdField);
      const email = emailField ? emailField.getAttribute('href').replace('mailto:', '') : '';
      const retailerName = retailerNameField ? this.extractRetailerName(retailerNameField) : '';

      if (retailerId) {
        this.updateButtonOnclick(button, { 
          id: retailerId,
          email: email,
          retailerName: retailerName
        });
        button.dataset.parameterized = 'true';
      }
    });
  }

  MOBILE_BREAKPOINT = 960;
  MIN_DRAWER_HEIGHT = 100;
  MAX_DRAWER_HEIGHT_RATIO = 0.9;
  PANEL_ID = 'strmpr-event-detail-panel';

  formatDateCanonical(date) {
    if (!date || !(date instanceof Date) || isNaN(date)) return '';
    const months = [
      'January','February','March','April','May','June',
      'July','August','September','October','November','December'
    ];
    return `${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
  }

  parseDateFromRawText(rawText) {
    if (!rawText) return { dateStr: '', date: null };

    let s = rawText.toString().replace(/\u00A0/g, ' ').trim();
    s = s.replace(/^[^:]*:\s*/, '').trim();            // drop labels before colon
    s = s.replace(/(\d)(st|nd|rd|th)/gi, '$1');        // remove ordinals
    s = s.replace(/\s+/g, ' ').trim();

    const monthNames = [
      'January','February','March','April','May','June',
      'July','August','September','October','November','December'
    ];
    const monthAlt = monthNames.concat(monthNames.map(m => m.slice(0,3))).map(m => m.replace('.', '')).join('|');
    const monthMap = monthNames.reduce((acc, m, i) => {
      acc[m.toLowerCase()] = i;
      acc[m.slice(0,3).toLowerCase()] = i;
      return acc;
    }, {});

    // 1) MM/DD/YYYY or M-D-YYYY or M.D.YYYY
    let m = s.match(/^\s*(\d{1,2})[\/\-\.\s](\d{1,2})[\/\-\.\s](\d{4})\s*$/);
    if (m) {
      const month = parseInt(m[1], 10) - 1;
      const day = parseInt(m[2], 10);
      const year = parseInt(m[3], 10);
      const dt = new Date(year, month, day);
      if (!isNaN(dt)) return { dateStr: this.formatDateCanonical(dt), date: dt };
    }

    // 2) YYYY-MM-DD or YYYY/MM/DD
    m = s.match(/^\s*(\d{4})[\/\-\.\s](\d{1,2})[\/\-\.\s](\d{1,2})\s*$/);
    if (m) {
      const year = parseInt(m[1], 10);
      const month = parseInt(m[2], 10) - 1;
      const day = parseInt(m[3], 10);
      const dt = new Date(year, month, day);
      if (!isNaN(dt)) return { dateStr: this.formatDateCanonical(dt), date: dt };
    }

    // 3) "MonthName D, YYYY" or "Mon D YYYY"
    let mn = s.match(new RegExp(`^\\s*(${monthAlt})\\.?\\s+(\\d{1,2}),?\\s*(\\d{4})\\s*$`, 'i'));
    if (mn) {
      const monthText = mn[1].replace('.', '').trim().toLowerCase();
      const day = parseInt(mn[2], 10);
      const year = parseInt(mn[3], 10);
      const monthIndex = monthMap[monthText];
      if (monthIndex !== undefined) {
        const dt = new Date(year, monthIndex, day);
        if (!isNaN(dt)) return { dateStr: this.formatDateCanonical(dt), date: dt };
      }
    }

    // 4) "D MonthName YYYY"
    let dm = s.match(new RegExp(`^\\s*(\\d{1,2})\\s+(${monthAlt})\\.?\\,?\\s*(\\d{4})\\s*$`, 'i'));
    if (dm) {
      const day = parseInt(dm[1], 10);
      const monthText = dm[2].replace('.', '').trim().toLowerCase();
      const year = parseInt(dm[3], 10);
      const monthIndex = monthMap[monthText];
      if (monthIndex !== undefined) {
        const dt = new Date(year, monthIndex, day);
        if (!isNaN(dt)) return { dateStr: this.formatDateCanonical(dt), date: dt };
      }
    }

    // 5) Partial mm/dd or "Month D" without year -> assume current year
    let partial = s.match(/^\s*(\d{1,2})[\/\-\.\s](\d{1,2})\s*$/);
    if (partial) {
      const month = parseInt(partial[1], 10);
      const day = parseInt(partial[2], 10);
      const year = (new Date()).getFullYear();
      if (month >= 1 && month <= 12) {
        const dt = new Date(year, month - 1, day);
        if (!isNaN(dt)) return { dateStr: this.formatDateCanonical(dt), date: dt };
      }
    }

    let mnPartial = s.match(new RegExp(`^\\s*(${monthAlt})\\.?\\s+(\\d{1,2})\\s*$`, 'i'));
    if (mnPartial) {
      const monthText = mnPartial[1].replace('.', '').trim().toLowerCase();
      const day = parseInt(mnPartial[2], 10);
      const monthIndex = monthMap[monthText];
      const year = (new Date()).getFullYear();
      if (monthIndex !== undefined) {
        const dt = new Date(year, monthIndex, day);
        if (!isNaN(dt)) return { dateStr: this.formatDateCanonical(dt), date: dt };
      }
    }

    // 6) Safe fallback: try Date.parse only if a 4-digit year exists or contains letters
    if (/\b\d{4}\b/.test(s) || /[a-zA-Z]/.test(s)) {
      const parsed = new Date(s);
      if (!isNaN(parsed)) return { dateStr: this.formatDateCanonical(parsed), date: parsed };
    }

    return { dateStr: '', date: null };
  }

  //Helper Method: Resolve logo source
  resolveLogoSrc(logoImg, publicImageField) {
    if (logoImg?.src) return logoImg.src;
    if (!publicImageField) return '';

    const fullText = publicImageField.textContent.trim();
    const colonIndex = fullText.indexOf(':');
    const raw = colonIndex !== -1
      ? fullText.substring(colonIndex + 1).trim()
      : (fullText.match(/https?:\/\/[^\s]+/)?.[0] ?? '');

    return raw.replace(/["']/g, '').trim();
  }

  addDivBelowRequestBooking(resultDiv) {
    if (this.pageType !== 'find-retailer') return;

    const reqBookingDiv = resultDiv.querySelector('.strmpr-field-custom.req_booking');
    if (!reqBookingDiv) return;

    if (reqBookingDiv.nextElementSibling?.classList.contains('strmpr-upcoming-event')) return;

    const startDateField = resultDiv.querySelector('.strmpr-field-custom.event_start-date');
    const endDateField   = resultDiv.querySelector('.strmpr-field-custom.event_end-date');

    const startRaw = startDateField?.textContent?.trim() ?? '';
    const endRaw   = endDateField?.textContent?.trim() ?? '';

    if (!startRaw) return;
    
    const rawStartText = startRaw;
    const rawEndText = endRaw;

    let startParsed = this.parseDateFromRawText(startRaw);
    if (!startParsed.date) {
      try {
        const cleanedStart = startRaw.replace(/\s*,\s*/, ', ').replace(/\u00A0/g, ' ').trim();
        const parsedDirect = new Date(cleanedStart);
        if (!isNaN(parsedDirect)) {
          startParsed = { dateStr: this.formatDateCanonical(parsedDirect), date: parsedDirect };
        }
      } catch (e) { /* ... */ }
    }

    if (!startParsed.date) return;

    const eventDate = startParsed.date;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (eventDate < today) return;

    let endParsed = this.parseDateFromRawText(endRaw);

    if (!endParsed.date && endRaw) {
      try {
        const cleanedEnd = endRaw.replace(/\s*,\s*/, ', ').replace(/\u00A0/g, ' ').trim();
        const parsedDirectEnd = new Date(cleanedEnd);
        if (!isNaN(parsedDirectEnd)) {
          endParsed = { dateStr: this.formatDateCanonical(parsedDirectEnd), date: parsedDirectEnd };
        }
      } catch (e) { /* ... */ }
    }

    const hasValidEndDate = !!endParsed.date;

    let eventDateText = '';
    const fmt = this.formatRetailerEventDateRange(rawStartText, rawEndText);
    if (fmt) {
      eventDateText = fmt;
    } else if (startParsed.date) {
      eventDateText = this.formatDateCanonical(startParsed.date);
    } else {
      eventDateText = rawStartText || '';
    }
    
    const upcomingEventDiv = this.createElement('button', 'strmpr-upcoming-event', [], `
      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none" class="calendar-icon">
        <path d="M18 1.62H14.08V0H13.16V1.62H4.85V0H3.93V1.62H0V18H18V1.62ZM17.08 17.11H0.92V6.33H17.07V17.1L17.08 17.11ZM17.08 5.44H0.92V2.52H3.92V4.09H4.84V2.52H13.15V4.09H14.07V2.52H17.07V5.44H17.08Z" fill="currentColor"></path>
        <path d="M3 11.27H4.62V12.17H3V11.27Z" fill="currentColor"></path>
        <path d="M3 13.96H4.62V14.86H3V13.96Z" fill="currentColor"></path>
        <path d="M6.46002 8.56995H8.08002V9.46995H6.46002V8.56995Z" fill="currentColor"></path>
        <path d="M6.46002 11.27H8.08002V12.17H6.46002V11.27Z" fill="currentColor"></path>
        <path d="M6.46002 13.96H8.08002V14.86H6.46002V13.96Z" fill="currentColor"></path>
        <path d="M9.91998 8.56995H11.54V9.46995H9.91998V8.56995Z" fill="currentColor"></path>
        <path d="M9.91998 11.27H11.54V12.17H9.91998V11.27Z" fill="currentColor"></path>
        <path d="M9.91998 13.96H11.54V14.86H9.91998V13.96Z" fill="currentColor"></path>
        <path d="M13.38 8.56995H15V9.46995H13.38V8.56995Z" fill="currentColor"></path>
        <path d="M13.38 11.27H15V12.17H13.38V11.27Z" fill="currentColor"></path>
      </svg>
      <div class="upcoming-event-content">
        <span class="upcoming-event-title">Upcoming Event</span>
        <p class="upcoming-event-date">${eventDateText}</p>
      </div>
      <div class="svg-wrapper">
        <svg class="icon icon-arrow" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
          <g id="Icons/Directional-Navigation/Chevron-Right">
            <path id="Vector" d="M9.5 17L14.5 12L9.5 7" stroke="#58514C" stroke-linecap="round" stroke-linejoin="round"></path>
          </g>
        </svg>
      </div>
    `);

    upcomingEventDiv.addEventListener('click', (e) => {
      e.stopPropagation();
      this.openEventDetailPanel(resultDiv);
    });

    reqBookingDiv.insertAdjacentElement('afterend', upcomingEventDiv);
  }

  setupEventDetailPanel() {
    const mapContainer = document.querySelector('.strmpr-layout-horizontal .strmpr-map');
    if (!mapContainer) return;

    let panel = document.getElementById(this.PANEL_ID);
    if (!panel) {
      panel = this.createElement('div', 'strmpr-event-detail-panel', [], `
        <div class="strmpr-panel-overlay"></div>
        <div class="strmpr-panel-resizer">
          <div class="strmpr-panel-resizer-line">&nbsp;</div>
        </div>
        <button class="strmpr-panel-close" aria-label="Close panel">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
          </svg>
        </button>
        <div class="strmpr-panel-content">
          <div class="strmpr-panel-body"></div>
        </div>
      `);
      panel.id = this.PANEL_ID;

      this.attachPanelToCorrectParent(panel);

      panel.querySelector('.strmpr-panel-overlay')
        .addEventListener('click', () => this.closeEventDetailPanel());

      panel.querySelector('.strmpr-panel-close')
        .addEventListener('click', () => this.closeEventDetailPanel());

      this.setupDrawerResizer(panel);

      let resizeTimer;
      window.addEventListener('resize', () => {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(() => this.attachPanelToCorrectParent(panel), 150);
      });
    }
  }

  attachPanelToCorrectParent(panel) {
    const mapContainer = document.querySelector('.strmpr-layout-horizontal .strmpr-map');
    const isMobile = window.innerWidth <= this.MOBILE_BREAKPOINT;

    if (isMobile) {
      if (panel.parentElement !== document.body) {
        document.body.appendChild(panel);
        panel.classList.add('strmpr-drawer-mode');
        panel.classList.remove('strmpr-panel-mode');
      }
    } else {
      if (mapContainer && panel.parentElement !== mapContainer) {
        mapContainer.appendChild(panel);
        panel.classList.add('strmpr-panel-mode');
        panel.classList.remove('strmpr-drawer-mode');
      }
    }
  }

  setupDrawerResizer(panel) {
    const resizer = panel.querySelector('.strmpr-panel-resizer');
    if (!resizer) return;

    let startY = 0;
    let startHeight = 0;
    let isDragging = false;

    const onStart = (e) => {
      if (!panel.classList.contains('strmpr-drawer-mode')) return;
      isDragging = true;
      startY = e.touches ? e.touches[0].clientY : e.clientY;
      startHeight = panel.offsetHeight;
      document.addEventListener('mousemove', onMove);
      document.addEventListener('touchmove', onMove, { passive: false });
      document.addEventListener('mouseup', onEnd);
      document.addEventListener('touchend', onEnd);
    };

    const onMove = (e) => {
      if (!isDragging) return;
      e.preventDefault();
      const currentY = e.touches ? e.touches[0].clientY : e.clientY;
      const diff = startY - currentY;
      const newHeight = Math.min(
        Math.max(startHeight + diff, this.MIN_DRAWER_HEIGHT),
        window.innerHeight * this.MAX_DRAWER_HEIGHT_RATIO
      );
      panel.style.height = `${newHeight}px`;
    };

    const onEnd = () => {
      isDragging = false;
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('touchmove', onMove);
      document.removeEventListener('mouseup', onEnd);
      document.removeEventListener('touchend', onEnd);
    };

    resizer.addEventListener('mousedown', onStart);
    resizer.addEventListener('touchstart', onStart, { passive: true });
  }

  openEventDetailPanel(resultDiv) {
    this.setupEventDetailPanel();

    const panel = document.getElementById(this.PANEL_ID);
    const panelBody = panel?.querySelector('.strmpr-panel-body');
    if (!panelBody) return;

    const nameField        = resultDiv.querySelector('.strmpr-field-name');
    const logoImg          = resultDiv.querySelector('.strmpr-field-logo img');
    const publicImageField = resultDiv.querySelector('.strmpr-field-custom.public_image_url');
    const startDateField   = resultDiv.querySelector('.strmpr-field-custom.event_start-date');
    const endDateField     = resultDiv.querySelector('.strmpr-field-custom.event_end-date');
    const collectionField  = resultDiv.querySelector('.strmpr-field-custom.strmpr_collection--list');
    const descriptionField = resultDiv.querySelector('.strmpr-field-description');

    const retailerName   = nameField ? this.extractRetailerName(nameField) : '';
    const logoSrc        = this.resolveLogoSrc(logoImg, publicImageField);
    const collectionText = collectionField
      ? collectionField.textContent.replace('Collection:', '').trim()
      : '';
    const descriptionText = descriptionField
      ? descriptionField.textContent.trim()
      : '';

    let eventDateText = '';
    if (startDateField && endDateField) {
      const stripLabel = (txt) => (txt || '').toString().replace(/^[^:]*:\s*/, '').replace(/\u00A0/g, ' ').trim();

      const rawStartText = stripLabel(startDateField.textContent);
      const rawEndText = stripLabel(endDateField.textContent);

      const splitRange = (text) => {
        if (!text) return [text];
        const parts = text.split(/\s*[–—-]\s*/).map(p => p.trim()).filter(Boolean);
        return parts.length > 1 ? parts : [text];
      };

      const startCandidates = splitRange(rawStartText);
      const endCandidates = splitRange(rawEndText);

      let startRaw = startCandidates[0] || '';
      let endRaw = endCandidates.length > 1 ? endCandidates[endCandidates.length - 1] :
                  (startCandidates.length > 1 ? startCandidates[startCandidates.length - 1] : rawEndText || '');

      let startParsed = this.parseDateFromRawText(startRaw);
      let endParsed = this.parseDateFromRawText(endRaw);

      if (!startParsed.date && endParsed.date) {
        try {
          const candidate = `${startRaw}, ${endParsed.date.getFullYear()}`;
          const parsedDirect = new Date(candidate);
          if (!isNaN(parsedDirect)) {
            startParsed = { dateStr: this.formatDateCanonical(parsedDirect), date: parsedDirect };
          }
        } catch (e) { /* ignore */ }
      }

      if (!endParsed.date && startParsed.date) {
        try {
          const candidate = `${endRaw}, ${startParsed.date.getFullYear()}`;
          const parsedDirect = new Date(candidate);
          if (!isNaN(parsedDirect)) {
            endParsed = { dateStr: this.formatDateCanonical(parsedDirect), date: parsedDirect };
          }
        } catch (e) { /* ignore */ }
      }

      const monthNames = [
        'January','February','March','April','May','June',
        'July','August','September','October','November','December'
      ];

      if (startParsed.date && endParsed.date) {
        const s = startParsed.date, e = endParsed.date;
        if (s.getFullYear() === e.getFullYear()) {
          eventDateText = `${monthNames[s.getMonth()]} ${s.getDate()} - ${monthNames[e.getMonth()]} ${e.getDate()}, ${s.getFullYear()}`;
        } else {
          eventDateText = `${monthNames[s.getMonth()]} ${s.getDate()}, ${s.getFullYear()} - ${monthNames[e.getMonth()]} ${e.getDate()}, ${e.getFullYear()}`;
        }
      } else if (startParsed.date) {
        const s = startParsed.date;
        eventDateText = `${monthNames[s.getMonth()]} ${s.getDate()}, ${s.getFullYear()}`;
      } else if (endParsed.date) {
        const e = endParsed.date;
        if (/[A-Za-z]/.test(startRaw)) {
          eventDateText = `${startRaw} - ${monthNames[e.getMonth()]} ${e.getDate()}, ${e.getFullYear()}`;
        } else {
          eventDateText = `${monthNames[e.getMonth()]} ${e.getDate()}, ${e.getFullYear()}`;
        }
      } else {
        const fmt = this.formatRetailerEventDateRange(startRaw, endRaw);
        eventDateText = fmt || (startRaw || '');
      }
      eventDateText = (eventDateText || '').replace(/,\s*,+/g, ',').replace(/,\s*$/, '').trim();
    }
    panelBody.innerHTML = `
      ${logoSrc ? `<div class="strmpr-panel-image">
        <img src="${logoSrc}" alt="${retailerName}" loading="lazy" onerror="this.parentElement.style.display='none'">
      </div>` : ''}
      <div class="strmpr-panel-info">
        ${retailerName    ? `<h3 class="strmpr-panel-retailer">${retailerName}</h3>` : ''}
        ${eventDateText   ? `<p class="strmpr-panel-dates">${eventDateText}</p>` : ''}
        ${collectionText  ? `<div class="strmpr-panel-collection-section">
          <p class="strmpr-panel-collection-title">Collections</p>
          <p class="strmpr-panel-collection">${collectionText}</p>
        </div>` : ''}
        ${descriptionText ? `<p class="strmpr-panel-description">${descriptionText}</p>` : ''}
      </div>
    `;

    this.attachPanelToCorrectParent(panel);

    if (window.innerWidth <= this.MOBILE_BREAKPOINT) {
      panel.style.height = '65%';
    }

    panel.classList.add('active');
  }

  closeEventDetailPanel() {
    const panel = document.getElementById(this.PANEL_ID);
    if (!panel) return;
    panel.classList.remove('active');
    setTimeout(() => { panel.style.height = ''; }, 300);
  }
  /**
   * Extract retailer name excluding store number
   */
  extractRetailerName(nameField) {
    // Clone the element to avoid modifying the original
    const tempElement = nameField.cloneNode(true);
    
    // Remove the store number span
    const storeNumberSpan = tempElement.querySelector('.store-number');
    if (storeNumberSpan) {
      storeNumberSpan.remove();
    }
    
    let retailerName = tempElement.textContent.trim();
    retailerName = retailerName.replace(/\s+/g, ' ').trim();
    
    return retailerName;
  }

  /**
   * Custom URL encoding that handles apostrophes properly
   */
  customEncodeURIComponent(str) {
    let encoded = encodeURIComponent(str.toString().trim());
    encoded = encoded.replace(/'/g, '%27');
    return encoded;
  }

  /**
   * Update button onclick with parameters
   */
  updateButtonOnclick(button, params) {
    const originalOnclick = button.getAttribute('onclick');
    const urlMatch = originalOnclick?.match(/window\.open\(['"]([^'"]+)['"]/);
    
    if (!urlMatch) return;

    const baseUrl = urlMatch[1];
    
    // Build parameter strings with proper encoding
    const paramStrings = Object.entries(params)
      .filter(([, value]) => value && value.toString().trim() !== '')
      .map(([key, value]) => {
        const encodedValue = this.customEncodeURIComponent(value);
        return `${key}=${encodedValue}`;
      });

    if (paramStrings.length === 0) return;

    const separator = baseUrl.includes('?') ? '&' : '?';
    const newUrl = baseUrl + separator + paramStrings.join('&');
    
    const newOnclick = originalOnclick.replace(baseUrl, newUrl);
    
    button.setAttribute('onclick', newOnclick);
  }

  /**
   * Extract field value from custom field
   */
  extractFieldValue(field) {
    return field ? field.textContent.trim().split(': ')[1] || '' : '';
  }

  /**
   * Setup logo image modal
   */
  setupLogoImageModal(resultDiv) {
    const logoImg = resultDiv.querySelector('.strmpr-field-logo img');
    if (!logoImg || this.processedElements.has(logoImg)) return;

    logoImg.style.cursor = 'pointer';
    logoImg.addEventListener('click', (event) => {
      event.stopPropagation();
      this.showImageModal(logoImg.src);
    });

    this.processedElements.add(logoImg);
  }

  /**
   * Process view more button
   */
  processViewMoreButton() {
    const viewMoreButton = document.querySelector('.strmpr-view-more-stores-button');
    viewMoreButton?.classList.add('button');
  }

  /**
   * Add dividers between results
   */
  addResultDividers() {
    // Remove existing dividers
    document.querySelectorAll('.strmpr-divider').forEach(divider => divider.remove());

    const items = document.querySelectorAll('#storemapper-list .strmpr-search-result');
    items.forEach((item, index) => {
      if (index !== items.length - 1) {
        const divider = this.createElement('div', 'strmpr-divider', [], ' ');
        item.parentNode.insertBefore(divider, item.nextSibling);
      }
    });
  }

  /**
   * Process event dates
   */
  processEventDates() {
    const eventStartDate = document.querySelectorAll('.strmpr-search-result .strmpr-field-custom.event_start-date');
    const eventEndDate = document.querySelectorAll('.strmpr-search-result .strmpr-field-custom.event_end-date');

    eventStartDate.forEach((startElement, index) => {
      const endElement = eventEndDate[index];
      if (!endElement) return;
  
      const startText = startElement.textContent || '';
      const endText = endElement.textContent || '';
  
      const formattedDate = this.formatRetailerEventDateRange(startText, endText);
      if (formattedDate) {
        startElement.textContent = formattedDate;
      }
    });
  }

  formatRetailerEventDateRange(startText, endText = '') {
    const startParsed = this.parseDateFromRawText(startText);
    if (!startParsed.date) return null;
  
    const endParsed = endText ? this.parseDateFromRawText(endText) : { date: null };
  
    const s = startParsed.date;
    const e = endParsed.date || null;
  
    if (!e) {
      return this.formatDateCanonical(s);
    }
  
    if (s.getFullYear() === e.getFullYear()) {
      const startMonth = s.toLocaleString('default', { month: 'long' });
      const endMonth = e.toLocaleString('default', { month: 'long' });
      const year = s.getFullYear();
      return `${startMonth} ${s.getDate()} - ${endMonth} ${e.getDate()}, ${year}`;
    }
    return `${this.formatDateCanonical(s)} - ${this.formatDateCanonical(e)}`;
  }
  /**
   * Process event types
   */
  processEventTypes() {
    const eventTypes = document.querySelectorAll('.strmpr-search-result .strmpr-field-custom.strmpr_event-type');
    
    eventTypes.forEach(element => {
      const text = element.textContent.trim();
      const colonIndex = text.indexOf(':');
      
      if (colonIndex !== -1) {
        const valueAfterColon = text.substring(colonIndex + 1).trim();
        element.textContent = valueAfterColon;
      }
    });
  }

  /**
   * Setup image modal
   */
  setupImageModal() {
    let imageModal = document.getElementById('strmpr-image-modal');
    if (imageModal) return;

    imageModal = this.createElement('div', 'strmpr-image-modal', [], `
      <div class="strmpr-modal-content">
        <div class="strmpr-modal-inner">
          <span class="strmpr-modal-close">
            <svg width="21" height="20" viewBox="0 0 21 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="14.9194" y="4.99121" width="0.833333" height="13.3333" transform="rotate(45 14.9194 4.99121)" fill="#58514C"/>
              <path d="M15.5087 14.4194L14.9194 15.0087L5.49132 5.5806L6.08058 4.99134L15.5087 14.4194Z" fill="#58514C"/>
            </svg>
          </span>
          <img class="strmpr-modal-img" src="" alt="Store Image">
        </div>
      </div>
    `);

    imageModal.id = 'strmpr-image-modal';
    document.body.appendChild(imageModal);

    // Setup modal event listeners
    const closeBtn = imageModal.querySelector('.strmpr-modal-close');
    closeBtn.addEventListener('click', () => this.hideImageModal());
    
    imageModal.addEventListener('click', (event) => {
      if (event.target === imageModal) {
        this.hideImageModal();
      }
    });
  }

  /**
   * Show image modal
   */
  showImageModal(imageSrc) {
    const imageModal = document.getElementById('strmpr-image-modal');
    const modalImg = imageModal?.querySelector('.strmpr-modal-img');
    
    if (modalImg && imageModal) {
      modalImg.src = imageSrc;
      imageModal.style.display = 'block';
    }
  }

  /**
   * Hide image modal
   */
  hideImageModal() {
    const imageModal = document.getElementById('strmpr-image-modal');
    if (imageModal) {
      imageModal.style.display = 'none';
    }
  }

  /**
   * Handle map design - adjust grid row end based on search state
   */
  handleMapDesign() {
    const { mapContainer } = this.elements;
    const errorElement = document.getElementById('storemapper-error');
    const storemapperList = document.getElementById('storemapper-list');

    if (!mapContainer) return;

    // Primary check: Set grid row end based on storemapper-list children count
    if (storemapperList) {
      if (storemapperList.children.length <= 1) {
        mapContainer.style.gridRowEnd = '5';
      } else {
        mapContainer.style.gridRowEnd = '4';
      }
    }

    // Set grid row end if there's an error message
    if (errorElement && errorElement.innerHTML.trim() !== '') {
      mapContainer.style.gridRowEnd = '5';
    }
  }

  /**
   * Handle map design on search button click
   */
  handleMapDesignOnSearch() {
    const { mapContainer } = this.elements;
    if (!mapContainer) return;

    // Recheck storemapper-list after search with a slight delay
    setTimeout(() => {
      const storemapperList = document.getElementById('storemapper-list');
      if (storemapperList) {
        if (storemapperList.children.length <= 1) {
          mapContainer.style.gridRowEnd = '5';
        } else {
          mapContainer.style.gridRowEnd = '4';
        }
      }
    }, 100);
  }

  /**
   * Handle map design on reset button click
   */
  handleMapDesignOnReset() {
    const { mapContainer } = this.elements;
    if (!mapContainer) return;

    // After reset, list should be empty, so set to 5
    mapContainer.style.gridRowEnd = '5';
  }

  /**
   * Create element helper
   */
  createElement(tag, className = '', children = [], textContent = '') {
    const element = document.createElement(tag);
    
    if (className) {
      if (Array.isArray(className)) {
        element.classList.add(...className);
      } else {
        element.className = className;
      }
    }
    
    if (textContent) {
      element.innerHTML = textContent;
    }
    
    children.forEach(child => {
      if (child) element.appendChild(child);
    });
    
    return element;
  }

  /**
   * Cleanup observers
   */
  destroy() {
    this.observers.forEach(observer => observer.disconnect());
    this.observers.clear();
    clearTimeout(this.functionalitiesTimeout);
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  new StoreMapper();
});